import { yupResolver } from "@hookform/resolvers/yup";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import {
	Avatar,
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Grid,
	IconButton,
	Radio,
	RadioGroup,
	TextField,
	Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { lngs, supportedLanguages } from "../../../constants";
import { useGetBarDataQuery, useUpdateBarMutation } from "../infoBarApiSlice";
import { getFormSchema } from "./formSchema";
import { Preview } from "./InfoBarPreview";

const defaultMessages = supportedLanguages.reduce((acc, lang) => {
	acc[`message-${lang}`] = "";
	return acc;
}, {});

export const InfoBarPanel = () => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [updateBarMutation, { isLoading: isUpdating }] = useUpdateBarMutation();

	const { data: barData, isLoading, isFetching } = useGetBarDataQuery({});

	const {
		handleSubmit,
		control,
		formState: { errors },
		reset,
		setError,
		watch,
		setValue,
		getValues,
	} = useForm({
		resolver: yupResolver(getFormSchema(t)),
		shouldFocusError: true,
		defaultValues: {
			...defaultMessages,
			type: "success",
			isVisible: true,
			links: [],
		},
	});

	const watchedLinks = watch("links") || [];

	const addLink = () => {
		const currentLinks = getValues("links") || [];
		if (currentLinks.length < 2) {
			setValue("links", [...currentLinks, { url: "", text: "" }]);
		}
	};

	const removeLink = (index) => {
		const currentLinks = getValues("links") || [];
		const newLinks = currentLinks.filter((_, i) => i !== index);
		setValue("links", newLinks);
	};

	const updateLink = (index, field, value) => {
		const currentLinks = getValues("links") || [];
		const newLinks = [...currentLinks];
		newLinks[index] = { ...newLinks[index], [field]: value };
		setValue("links", newLinks);
	};

	const handleSaveBar = async (data) => {
		// The data object now includes:
		// - message-pl, message-en, message-cz, ... (text for each language)
		// - type (success, info, warning, error)
		// - isVisible (boolean)
		// - links (array of {text, url} objects, max 2)

		const { links, isVisible, type, ...rest } = data;
		const messages = Object.fromEntries(
			Object.entries(rest).filter(([key]) => key.startsWith("message-"))
		);
		// transformed data to squeeze all messages into a single object
		const parsedData = { messages, links, isVisible, type };

		await updateBarMutation({ data: parsedData })
			.unwrap()
			.then((_payload) => {
				enqueueSnackbar(t("updated_data"), {
					variant: "success",
				});
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([key, message]) => {
						setError(key);
						enqueueSnackbar(message[0], {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
	};

	useEffect(() => {
		if (Object.keys(errors).length > 0) {
			Object.values(errors).forEach((err) => {
				if (Array.isArray(err)) {
					err.forEach((e) => {
						Object.values(e).forEach(({ message }) => {
							enqueueSnackbar(message, {
								variant: "error",
								preventDuplicate: true,
							});
						});
					});
				} else {
					enqueueSnackbar(err.message, {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
		}
	}, [errors, enqueueSnackbar]);

	useEffect(() => {
		if (barData) {
			const messages = supportedLanguages.reduce((acc, lang) => {
				acc[`message-${lang}`] =
					barData?.data?.messages?.[`message-${lang}`] || "";
				return acc;
			}, {});

			reset({
				...messages,
				type: barData?.data?.type || "success",
				isVisible: barData?.data?.isVisible ? true : false,
				links: barData?.data?.links || [],
			});
		}
	}, [barData, reset]);

	if (isLoading || isFetching) {
		return "Loading...";
	}

	return (
		<Box sx={{ display: "flex", gap: 2 }}>
			<Box sx={{ flex: "1 1 auto" }}>
				<Box component="form" noValidate onSubmit={handleSubmit(handleSaveBar)}>
					<Grid container spacing={1.5}>
						<Grid size={12}>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
								<Box>
									<Typography>Tekst</Typography>
									<Box
										sx={{ display: "flex", flexDirection: "column", gap: 1 }}
									>
										{Object.entries(lngs).map(([code, { flagUrl }]) => (
											<Input
												required
												key={code}
												control={control}
												name={`message-${code}`}
												flag={flagUrl}
												error={!!errors[`message-${code}`]}
											/>
										))}
									</Box>
								</Box>
								<Box>
									<Typography>Typ</Typography>
									<Controller
										control={control}
										name="type"
										defaultValue="success"
										render={({ field: { onChange, value } }) => (
											<RadioGroup value={value} onChange={onChange}>
												<FormControlLabel
													value="success"
													sx={{
														"& .MuiTypography-root": {
															color: "#2e7d32",
														},
													}}
													control={
														<Radio
															sx={{
																color: "red !important",
																"& .MuiSvgIcon-root": {
																	color: "#2e7d32",
																},
															}}
														/>
													}
													label="Success"
												/>
												<FormControlLabel
													value="info"
													sx={{
														"& .MuiTypography-root": {
															color: "#0288d1",
														},
													}}
													control={
														<Radio
															sx={{
																color: "red !important",
																"& .MuiSvgIcon-root": {
																	color: "#0288d1",
																},
															}}
														/>
													}
													label="Info"
												/>
												<FormControlLabel
													value="warning"
													sx={{
														"& .MuiTypography-root": {
															color: "#ed6c02",
														},
													}}
													control={
														<Radio
															sx={{
																color: "red !important",
																"& .MuiSvgIcon-root": {
																	color: "#ed6c02",
																},
															}}
														/>
													}
													label="Warning"
												/>
												<FormControlLabel
													value="error"
													sx={{
														"& .MuiTypography-root": {
															color: "#d32f2f",
														},
													}}
													control={
														<Radio
															sx={{
																color: "red !important",
																"& .MuiSvgIcon-root": {
																	color: "#d32f2f",
																},
															}}
														/>
													}
													label="Error"
												/>
											</RadioGroup>
										)}
									/>
								</Box>
								<Box>
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											mb: 1,
										}}
									>
										<Typography>Linki (max 2)</Typography>
										{watchedLinks.length < 2 && (
											<Button
												startIcon={<AddIcon />}
												onClick={addLink}
												size="small"
												variant="outlined"
											>
												Dodaj link
											</Button>
										)}
									</Box>
									<Box
										sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
									>
										{watchedLinks.map((link, index) => (
											<Box
												key={index}
												sx={{ display: "flex", gap: 1, alignItems: "center" }}
											>
												<TextField
													size="small"
													label="Tekst linku"
													value={link.text || ""}
													onChange={(e) =>
														updateLink(index, "text", e.target.value)
													}
													sx={{ flex: 1 }}
													error={errors?.links && !!errors?.links[index]?.text}
												/>
												<TextField
													size="small"
													label="URL"
													type="url"
													value={link.url || ""}
													onChange={(e) =>
														updateLink(index, "url", e.target.value)
													}
													sx={{ flex: 1 }}
													placeholder="https://example.com"
													error={errors?.links && !!errors?.links[index]?.url}
												/>
												<IconButton
													onClick={() => removeLink(index)}
													color="error"
													size="small"
												>
													<DeleteIcon />
												</IconButton>
											</Box>
										))}
									</Box>
								</Box>
								<Box>
									<Typography>Widoczność</Typography>
									<Controller
										control={control}
										name="isVisible"
										render={({ field: { onChange, value } }) => (
											<FormControlLabel
												label={"Pokaż/ukryj"}
												control={
													<Checkbox checked={!!value} onChange={onChange} />
												}
											/>
										)}
									/>
								</Box>
							</Box>
						</Grid>
						<Grid size={12}>
							<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
								<Button loading={isUpdating} type="submit" variant="contained">
									{t("save")}
								</Button>
							</Box>
						</Grid>
					</Grid>
				</Box>
			</Box>
			<Preview watch={watch} />
		</Box>
	);
};

const Input = ({ control, name, error, flag }) => {
	return (
		<Controller
			control={control}
			name={name}
			render={({ field: { onChange, value } }) => (
				<Box
					sx={{
						display: "flex",
						gap: "10px",
						alignItems: "center",
						width: "100%",
					}}
				>
					<Box
						sx={{
							flex: "1",
							display: "flex",
							alignItems: "center",
						}}
					>
						<TextField
							size="small"
							value={value}
							variant="outlined"
							fullWidth
							onChange={onChange}
							error={error}
							slotProps={{
								input: {
									sx: { paddingLeft: 0, overflow: "hidden" },
									startAdornment: (
										<Box sx={{ backgroundColor: "#f2f2f2", p: 1, mr: 1 }}>
											<Avatar src={flag} sx={{ width: 18, height: 18 }} />
										</Box>
									),
								},
								inputLabel: { shrink: true },
							}}
						/>
					</Box>
				</Box>
			)}
		/>
	);
};
